import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner, Nav, Modal } from 'react-bootstrap';
import { XCircle, Download, RefreshCw, Copy, Check, Phone, MessageCircle } from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";
import {
    getWhatsappSupport,
    getWhatsappSupportStats,
    updateWhatsappSupport,
    exportWhatsappData
} from '../../actions/whatsappAnalytics';
import { GET_WA_SUPPORT } from '../../actions/types';
import { setAlert } from '../../actions/alert';
import DateRangeBar, { useDateRange } from './DateRangeBar';
import Pager from './Pager';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';
import Metric from './Metric';
import { downloadXlsx } from './exportXlsx';
import {
    SUPPORT_STATUS_LABELS,
    SUPPORT_TYPE_LABELS,
    supportTypeLabel,
    issueCategoryLabel,
    journeyLabel,
    stageLabel,
    formatAge,
    formatHours,
    formatIstDateTime
} from './labels';

const PAGE_SIZE = 20;
const NO_VALUE = '-';
const REFRESH_MS = 60000;
const SEARCH_DEBOUNCE_MS = 400;
const COPIED_MS = 1500;
const WARN_HOURS = 24;
const DANGER_HOURS = 72;
const HOUR_MS = 3600000;
const NOTES_MAX_LENGTH = 1000;
const TIMELINE_PATH = '/inventory/v1.2/whatsapp/timeline';
const USER_PATH = '/inventory/v1.2/users';
const COUNTRY_CODE = '91';

const TABS = [
    { key: 'open', label: 'Open' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'resolved', label: 'Closed' },
    { key: 'dismissed', label: 'Dismissed' }
];

// Waiting requests are worked oldest first; finished ones are browsed newest first
const DEFAULT_SORT = { open: 'oldest', in_progress: 'oldest', resolved: 'newest', dismissed: 'newest' };

const STATUS_VARIANTS = { open: 'danger', in_progress: 'primary', resolved: 'success', dismissed: 'secondary' };

const START = { label: 'Mark in progress', short: 'Start', status: 'in_progress', variant: 'outline-primary' };
const CLOSE = { label: 'Mark closed', short: 'Close', status: 'resolved', variant: 'outline-success' };
const DISMISS = { label: 'Dismiss', short: 'Dismiss', status: 'dismissed', variant: 'outline-secondary' };
const REOPEN = { label: 'Reopen', short: 'Reopen', status: 'open', variant: 'outline-danger' };
const BACK_TO_OPEN = { label: 'Back to Open', short: 'Back to Open', status: 'open', variant: 'outline-danger' };

const ACTIONS_BY_STATUS = {
    open: [START, CLOSE, DISMISS],
    in_progress: [CLOSE, DISMISS, BACK_TO_OPEN],
    resolved: [REOPEN],
    dismissed: [REOPEN]
};

const QUICK_ACTIONS_BY_STATUS = {
    open: [START, CLOSE],
    in_progress: [CLOSE],
    resolved: [REOPEN],
    dismissed: [REOPEN]
};

const formatCount = (value) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : NO_VALUE);

const isWaiting = (status) => status === 'open' || status === 'in_progress';

const ageVariant = (row) => {
    if (!isWaiting(row.status)) return 'text-muted';
    const hours = (Date.now() - new Date(row.createdAt).getTime()) / HOUR_MS;
    if (hours >= DANGER_HOURS) return 'text-danger fw-semibold';
    if (hours >= WARN_HOURS) return 'text-warning fw-semibold';
    return '';
};

// Waiting requests show how long they have waited; finished ones show how long they took
const ageText = (row) => {
    if (isWaiting(row.status)) return formatAge(row.createdAt);
    return row.resolvedAt ? formatAge(row.createdAt, row.resolvedAt) : NO_VALUE;
};

const sourceText = (source) => {
    if (!source || (!source.journey && !source.stage)) return NO_VALUE;
    return [source.journey && journeyLabel(source.journey), source.stage && stageLabel(source.stage)].filter(Boolean).join(' / ');
};

const exportRow = (row) => ({
    'Created (IST)': formatIstDateTime(row.createdAt),
    Type: row.label || supportTypeLabel(row.supportType),
    'Issue category': row.categoryLabel || '',
    Status: SUPPORT_STATUS_LABELS[row.status] || row.status,
    'Closed at (IST)': row.resolvedAt ? formatIstDateTime(row.resolvedAt) : '',
    Customer: row.userName || '',
    Phone: row.phone || '',
    'User id': row.userId || ''
});

const CopyButton = ({ text, label }) => {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const copy = async (event) => {
        event.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), COPIED_MS);
        } catch (err) {
            console.error('Clipboard unavailable', err);
        }
    };

    return (
        <Button size="sm" variant="link" className="p-0 ms-1 align-baseline" onClick={copy} aria-label={label} title={label}>
            {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
        </Button>
    );
};

CopyButton.propTypes = {
    text: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
};

const PhoneCell = ({ phone }) => {
    if (!phone) return <span className="text-muted">{NO_VALUE}</span>;
    const stop = (event) => event.stopPropagation();
    return (
        <span className="text-nowrap">
            {phone}
            <CopyButton text={phone} label="Copy phone number" />
            <a href={`tel:+${COUNTRY_CODE}${phone}`} onClick={stop} className="ms-2" aria-label="Call" title="Call">
                <Phone size={14} />
            </a>
            <a
                href={`https://wa.me/${COUNTRY_CODE}${phone}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stop}
                className="ms-2"
                aria-label="Open WhatsApp chat"
                title="Open WhatsApp chat"
            >
                <MessageCircle size={14} />
            </a>
        </span>
    );
};

PhoneCell.propTypes = {
    phone: PropTypes.string
};

const StatusBadge = ({ status }) => (
    <Badge bg={STATUS_VARIANTS[status] || 'secondary'}>{SUPPORT_STATUS_LABELS[status] || status}</Badge>
);

StatusBadge.propTypes = {
    status: PropTypes.string.isRequired
};

const DetailRow = ({ label, children }) => (
    <Row className="mb-2">
        <Col xs={4} className="text-muted">{label}</Col>
        <Col xs={8}>{children}</Col>
    </Row>
);

DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node
};

const RequestModal = ({ request, busy, onClose, onChange }) => {
    const [notes, setNotes] = useState(request.notes || '');

    useEffect(() => {
        setNotes(request.notes || '');
    }, [request.id, request.notes]);

    const notesChanged = notes.trim() !== (request.notes || '');

    return (
        <Modal show onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {supportTypeLabel(request.type)} <span className="text-muted fs-6">{request.reference}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DetailRow label="Status"><StatusBadge status={request.status} /></DetailRow>
                <DetailRow label="Customer">
                    {request.user ? (
                        <>
                            <div className="fw-semibold">{request.user.name || 'Unnamed'}</div>
                            <PhoneCell phone={request.user.phone} />
                            <div className="small mt-1">
                                <Link to={`${TIMELINE_PATH}/${request.user.id}`}>WhatsApp timeline</Link>
                                <span className="mx-2 text-muted">|</span>
                                <Link to={`${USER_PATH}/${request.user.id}`}>User page</Link>
                            </div>
                        </>
                    ) : (
                        <span className="text-muted">Deleted user</span>
                    )}
                </DetailRow>
                <DetailRow label="Issue category">{request.issueCategory ? issueCategoryLabel(request.issueCategory) : NO_VALUE}</DetailRow>
                <DetailRow label="Came from">{sourceText(request.source)}</DetailRow>
                <DetailRow label="Created (IST)">{formatIstDateTime(request.createdAt)}</DetailRow>
                {isWaiting(request.status) ? (
                    <DetailRow label="Waiting for"><span className={ageVariant(request)}>{ageText(request)}</span></DetailRow>
                ) : (
                    <>
                        <DetailRow label="Closed (IST)">{formatIstDateTime(request.resolvedAt)}</DetailRow>
                        <DetailRow label="Time taken">{ageText(request)}</DetailRow>
                    </>
                )}
                <Form.Group className="mt-3">
                    <Form.Label className="fw-bold">Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={notes}
                        maxLength={NOTES_MAX_LENGTH}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="What was said or done for this customer"
                    />
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="text-muted small">{notes.length}/{NOTES_MAX_LENGTH}</span>
                        <Button
                            size="sm"
                            variant="primary"
                            disabled={busy || !notesChanged}
                            onClick={() => onChange(request, { notes: notes.trim() })}
                        >
                            Save notes
                        </Button>
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div className="d-flex flex-wrap gap-2">
                    {ACTIONS_BY_STATUS[request.status].map(({ label, status, variant }) => (
                        <Button
                            key={status}
                            variant={variant}
                            disabled={busy}
                            onClick={() => onChange(request, { status, ...(notesChanged ? { notes: notes.trim() } : {}) })}
                        >
                            {label}
                        </Button>
                    ))}
                </div>
                <Button variant="light" onClick={onClose}>Close window</Button>
            </Modal.Footer>
        </Modal>
    );
};

RequestModal.propTypes = {
    request: PropTypes.object.isRequired,
    busy: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired
};

const SupportQueue = ({
    support, supportStats, pending, error,
    getWhatsappSupport, getWhatsappSupportStats, updateWhatsappSupport, exportWhatsappData, setAlert
}) => {
    const { range, setRange } = useDateRange();
    const [tab, setTab] = useState('open');
    const [sort, setSort] = useState(DEFAULT_SORT.open);
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [silent, setSilent] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        document.title = 'WhatsApp Support';
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const loadQueue = useCallback(() => (
        getWhatsappSupport({ page, limit: PAGE_SIZE, status: tab, type: typeFilter, search, sort })
    ), [getWhatsappSupport, page, tab, typeFilter, search, sort]);

    const loadStats = useCallback(() => (
        getWhatsappSupportStats({ from: range.from, to: range.to })
    ), [getWhatsappSupportStats, range.from, range.to]);

    useEffect(() => {
        loadQueue();
    }, [loadQueue]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const refreshRef = useRef(null);
    refreshRef.current = () => {
        setSilent(true);
        Promise.resolve(loadQueue()).finally(() => setSilent(false));
        loadStats();
    };

    useEffect(() => {
        if (!autoRefresh) return undefined;
        const timer = setInterval(() => {
            if (!document.hidden && !busyId) refreshRef.current();
        }, REFRESH_MS);
        return () => clearInterval(timer);
    }, [autoRefresh, busyId]);

    // A status change can empty the last page; step back instead of showing an empty table
    useEffect(() => {
        if (pending[GET_WA_SUPPORT] || page <= 1) return;
        if (support.pages === 0) setPage(1);
        else if (page > support.pages) setPage(support.pages);
    }, [pending, page, support.pages]);

    const selectTab = (key) => {
        setTab(key);
        setSort(DEFAULT_SORT[key]);
        setPage(1);
    };

    const clearFilters = () => {
        setTypeFilter('all');
        setSearchInput('');
        setSearch('');
        setSort(DEFAULT_SORT[tab]);
        setPage(1);
    };

    const changeRequest = async (row, changes) => {
        const id = row.id;
        setBusyId(id);
        try {
            const updated = await updateWhatsappSupport(id, changes);
            if (!updated) return;
            setAlert(changes.status ? `Marked ${SUPPORT_STATUS_LABELS[changes.status].toLowerCase()}` : 'Notes saved', 'success');
            setSelected((current) => (current && current.id === id ? updated : current));
            loadQueue();
            loadStats();
        } finally {
            setBusyId(null);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const rows = await exportWhatsappData('support', range);
            if (rows === null) return;
            if (rows.length === 0) {
                setAlert('No support requests in this range to export', 'info');
                return;
            }
            downloadXlsx(rows.map(exportRow), `whatsapp_support_${range.from}_to_${range.to}`);
        } finally {
            setExporting(false);
        }
    };

    const counts = support.counts || {};
    const stats = supportStats && supportStats.range.from === range.from && supportStats.range.to === range.to ? supportStats : null;
    const backlog = stats ? stats.backlog : null;
    const queueLoading = Boolean(pending[GET_WA_SUPPORT]) && !silent;
    const filtered = typeFilter !== 'all' || search !== '';

    const renderTable = () => {
        if (queueLoading) {
            return <LoadingState />;
        }
        if (support.rows.length === 0) {
            return (
                <EmptyState className="m-3">
                    {filtered ? 'No requests match these filters.' : `No ${SUPPORT_STATUS_LABELS[tab].toLowerCase()} requests.`}
                </EmptyState>
            );
        }
        return (
            <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                    <tr>
                        <th>Reference</th>
                        <th>Customer</th>
                        <th>Type</th>
                        <th>Issue category</th>
                        <th>Came from</th>
                        <th>{isWaiting(tab) ? 'Waiting' : 'Time taken'}</th>
                        <th>{isWaiting(tab) ? 'Status' : 'Closed (IST)'}</th>
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {support.rows.map((row) => (
                        <tr key={row.id} onClick={() => setSelected(row)} style={{ cursor: 'pointer' }}>
                            <td className="text-nowrap">
                                {row.reference}
                                <div className="text-muted small">{formatIstDateTime(row.createdAt)}</div>
                            </td>
                            <td>
                                {row.user ? (
                                    <>
                                        <Link to={`${TIMELINE_PATH}/${row.user.id}`} onClick={(e) => e.stopPropagation()} className="fw-semibold">
                                            {row.user.name || 'Unnamed'}
                                        </Link>
                                        <div><PhoneCell phone={row.user.phone} /></div>
                                    </>
                                ) : (
                                    <span className="text-muted">Deleted user</span>
                                )}
                            </td>
                            <td>{supportTypeLabel(row.type)}</td>
                            <td>{row.issueCategory ? issueCategoryLabel(row.issueCategory) : NO_VALUE}</td>
                            <td>{sourceText(row.source)}</td>
                            <td className={`text-nowrap ${ageVariant(row)}`}>{ageText(row)}</td>
                            <td className="text-nowrap">
                                {isWaiting(row.status) ? <StatusBadge status={row.status} /> : formatIstDateTime(row.resolvedAt)}
                            </td>
                            <td className="text-end text-nowrap">
                                {QUICK_ACTIONS_BY_STATUS[row.status].map(({ short, status, variant }) => (
                                    <Button
                                        key={status}
                                        size="sm"
                                        variant={variant}
                                        className="ms-1"
                                        disabled={busyId === row.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            changeRequest(row, { status });
                                        }}
                                    >
                                        {short}
                                    </Button>
                                ))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4 align-items-center g-3">
                <Col xs={12} lg>
                    <h2 className="mb-1">WhatsApp Support</h2>
                    <p className="text-muted mb-0">
                        Customers who asked for a call or help. The queue always shows every request; the date range below applies to the figures and the export.
                    </p>
                </Col>
                <Col xs={12} lg="auto">
                    <DateRangeBar range={range} onChange={setRange} />
                </Col>
            </Row>

            <Card className="shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col xs={6} md={4} xl><Metric label="Open" value={formatCount(counts.open)} /></Col>
                        <Col xs={6} md={4} xl><Metric label="In progress" value={formatCount(counts.in_progress)} /></Col>
                        <Col xs={6} md={4} xl>
                            <Metric
                                label="Longest waiting"
                                value={backlog && backlog.oldestAgeHours !== null ? formatHours(backlog.oldestAgeHours) : NO_VALUE}
                                hint="open and in progress"
                            />
                        </Col>
                        <Col xs={6} md={4} xl>
                            <Metric
                                label="Average time to close"
                                value={stats && stats.avgResolveHours !== null ? formatHours(stats.avgResolveHours) : NO_VALUE}
                                hint="requests created in range"
                            />
                        </Col>
                        <Col xs={6} md={4} xl>
                            <Metric label="Requests in range" value={stats ? formatCount(stats.total) : NO_VALUE} />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        <Col md={4} lg={3}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Type</Form.Label>
                                <Form.Select
                                    value={typeFilter}
                                    onChange={(e) => {
                                        setTypeFilter(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="all">All types</option>
                                    {Object.entries(SUPPORT_TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} lg={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Search</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Name, phone, user id or SR reference"
                                    maxLength={60}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} lg={2}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Order</Form.Label>
                                <Form.Select
                                    value={sort}
                                    onChange={(e) => {
                                        setSort(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="oldest">Oldest first</option>
                                    <option value="newest">Newest first</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col xs={12} lg className="d-flex flex-wrap gap-2 justify-content-lg-end">
                            <Button type="button" variant="outline-secondary" onClick={clearFilters}>
                                <XCircle className="me-2" size={16} />
                                Clear
                            </Button>
                            <Button
                                type="button"
                                variant="outline-primary"
                                onClick={handleExport}
                                disabled={exporting}
                                title="Exports every request created in the selected date range; the queue filters are not applied"
                            >
                                {exporting ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Exporting...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} className="me-2" />
                                        Export range (xlsx)
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <ErrorBanner message={error} onRetry={loadQueue} />}

            <Card className="shadow-sm">
                <Card.Header className="bg-white">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <Nav variant="tabs" activeKey={tab} onSelect={selectTab} className="border-0">
                            {TABS.map(({ key, label }) => (
                                <Nav.Item key={key}>
                                    <Nav.Link eventKey={key}>
                                        {label} <Badge bg={key === tab ? 'primary' : 'secondary'} pill>{formatCount(counts[key] || 0)}</Badge>
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        <div className="d-flex align-items-center gap-3">
                            <Form.Check
                                type="switch"
                                id="support-auto-refresh"
                                label="Auto refresh (60s)"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                            />
                            <Button size="sm" variant="outline-primary" onClick={() => refreshRef.current()} aria-label="Refresh queue">
                                <RefreshCw size={14} className="me-1" />
                                Refresh
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {renderTable()}
                    <Pager page={page} pages={support.pages} onChange={setPage} />
                </Card.Body>
                <Card.Footer className="bg-white text-muted small">
                    {formatCount(support.total)} {filtered ? 'matching' : SUPPORT_STATUS_LABELS[tab].toLowerCase()} request{support.total === 1 ? '' : 's'}
                </Card.Footer>
            </Card>

            {selected && (
                <RequestModal
                    request={selected}
                    busy={busyId === selected.id}
                    onClose={() => setSelected(null)}
                    onChange={changeRequest}
                />
            )}
        </Container>
    );
};

SupportQueue.propTypes = {
    support: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        total: PropTypes.number.isRequired,
        pages: PropTypes.number.isRequired,
        counts: PropTypes.object
    }).isRequired,
    supportStats: PropTypes.object,
    pending: PropTypes.object.isRequired,
    error: PropTypes.string,
    getWhatsappSupport: PropTypes.func.isRequired,
    getWhatsappSupportStats: PropTypes.func.isRequired,
    updateWhatsappSupport: PropTypes.func.isRequired,
    exportWhatsappData: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    support: state.whatsappAnalytics.support,
    supportStats: state.whatsappAnalytics.supportStats,
    pending: state.whatsappAnalytics.pending,
    error: state.whatsappAnalytics.error
});

export default connect(mapStateToProps, {
    getWhatsappSupport,
    getWhatsappSupportStats,
    updateWhatsappSupport,
    exportWhatsappData,
    setAlert
})(SupportQueue);
